import { defineMessages, type MessageDescriptor } from 'react-intl'

import { CLINICAL_MEASURES } from '@lyrahealth-inc/shared-app-logic/src/features/outcomes/types'
import { isCTPOForProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { type Outcome, type Scores } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { type ProviderProgramTaxonomy } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

/**
 * The single catalog of clinical measures the CurrentMeasuresTable can render.
 *
 * This file is *data only* — it answers "what measures exist and how are they presented".
 * Deciding which measures to show for a given client lives in `getMeasureRows.ts`.
 *
 * A measure entry can be reached two ways, and one entry serves both:
 *  - a score arrives on an Outcome        -> `scoreId` / `supplemental[].id`
 *  - an assignment declares it as pending -> its `clinicalMeasuresIncluded` token
 *
 * Adding a measure means adding one entry here.
 */

export type ScoreId = Extract<keyof Scores, string>

/**
 * String form of `CLINICAL_MEASURES`, so catalog entries can list plain tokens and still fail to
 * compile on a typo or on a token the enum does not know about.
 */
export type ClinicalMeasureToken = `${CLINICAL_MEASURES}`

/** How a supplemental score is rendered inside the description tooltip. */
export type SupplementalFormat =
  /** Raw number, optionally over `maxScore`. */
  | 'number'
  /** `score / 100` rendered as a percentage. */
  | 'percent'
  /** Passed through `outcomeUtils.convertOutcomeScoreToDescription`. */
  | 'description'

export type SupplementalDefinition = {
  id: ScoreId
  label: MessageDescriptor
  /** Renders as `score / maxScore` when set. Omit when there is no verified ceiling. */
  maxScore?: number
  format?: SupplementalFormat
  /**
   * Score key to describe with, when it differs from `id`.
   * Alliance scores all share the single 'SATISFACTION' band table.
   */
  descriptionScoreId?: string
  /** Hide the detail entirely when the score is 0 (e.g. no self-harm reported). */
  omitWhenZero?: boolean
}

export type MeasureContext = {
  programTaxonomy?: Partial<ProviderProgramTaxonomy> | null
  /** Lyra id of the signed-in provider, for own-client visibility rules. */
  providerId?: string
  isSupervisor?: boolean
}

export type MeasureDefinition = {
  /** Row identity. Usually equals the primary score key; grouped instruments may use a broader id. */
  id: string
  name: MessageDescriptor
  /** Primary score, shown in the Score column. Omit for measures that only have parts. */
  scoreId?: ScoreId
  /**
   * Ceiling for the `score / maxScore` display.
   *
   * Hardcoded on purpose. Seeded from `outcomeChartConstants[].ranges` upper bounds, but those
   * are *severity band* boundaries rather than ceilings, so each value is verified against the
   * instrument before being listed here. Omitted where unverified, or where the backend returns
   * an averaged score (a denominator is meaningless there — see CGSQ, PARENTING-TASK).
   */
  maxScore?: number
  /** `outcomeChartConstants[].name` for the matching chart. Absent when no chart exists. */
  chartKey?: string
  /**
   * `response_details[].groups` key used by View answers when it differs from, or exists without,
   * the primary score. Measures with a primary score default to `scoreId`.
   */
  responseDetailsKey?: string
  /** Response groups combined by View answers for a measure with separately grouped questions. */
  responseDetailsKeys?: readonly string[]
  /** Omit the aggregate score in View answers for measures with only individual ratings. */
  hideFinalScore?: boolean
  /** Assignment-token aliases. Omit when `clinicalMeasuresIncluded` uses this measure's `id`. */
  metadataTokens?: readonly ClinicalMeasureToken[]
  supplemental?: readonly SupplementalDefinition[]
  /** CHRT surfaces high-risk answers alongside its score. */
  hasCriticalResponses?: boolean
  visibleWhen?: (context: MeasureContext, outcome?: Outcome) => boolean
}

const MESSAGES = defineMessages({
  // Measure names
  auditC: {
    defaultMessage: 'AUDIT-C (Alcohol Use Disorders Identification Test-Concise)',
    description: 'Name of the AUDIT-C assessment',
  },
  cgsq: { defaultMessage: 'CGSQ', description: 'Name of the Caregiver Strain Questionnaire' },
  parentalStress: { defaultMessage: 'Parental Stress', description: 'Name of the parental stress assessment' },
  parentingTask: { defaultMessage: 'Parenting Task', description: 'Name of the parenting task assessment' },
  rcadsAnxiety: { defaultMessage: 'RCADS: Anxiety', description: 'Name of the RCADS anxiety measure' },
  rcadsDepression: { defaultMessage: 'RCADS: Depression', description: 'Name of the RCADS depression measure' },
  rcadsTotal: { defaultMessage: 'RCADS: Total', description: 'Name of the RCADS-25 assessment' },
  rcadsParentTotal: {
    defaultMessage: 'RCADS-P: Total',
    description: 'Name of the parent-report RCADS-25 assessment',
  },
  rcadsParentAnxiety: {
    defaultMessage: 'RCADS-P: Anxiety',
    description: 'Name of the parent-report RCADS anxiety measure',
  },
  rcadsParentDepression: {
    defaultMessage: 'RCADS-P: Depression',
    description: 'Name of the parent-report RCADS depression measure',
  },
  tfm: { defaultMessage: 'TFM', description: 'Name of the Timeline Followback Method assessment' },
  tlfbAlcohol: { defaultMessage: 'TLFB Alcohol', description: 'Name of the alcohol Timeline Followback assessment' },
  tlfbSubstances: {
    defaultMessage: 'TLFB Substances',
    description: 'Name of the substances Timeline Followback assessment',
  },
  bdd: { defaultMessage: 'BDD', description: 'Name of the binge drinking days assessment' },
  pacs: { defaultMessage: 'PACS', description: 'Name of the Penn Alcohol Craving Scale' },
  adcs: { defaultMessage: 'ADCS', description: 'Name of the ADCS assessment' },
  wsas: { defaultMessage: 'WSAS', description: 'Name of the Work and Social Adjustment Scale' },
  pcl5: { defaultMessage: 'PCL5', description: 'Name of the PCL-5 trauma assessment' },
  chrt: { defaultMessage: 'CHRT', description: 'Name of the Concise Health Risk Tracking assessment' },
  gad7: { defaultMessage: 'GAD-7 (Anxiety)', description: 'Name of the Generalized Anxiety Disorder assessment' },
  phq9: { defaultMessage: 'PHQ-9 (Depression)', description: 'Name of the Patient Health Questionnaire' },
  pss: { defaultMessage: 'PSS (Stress)', description: 'Name of the Perceived Stress Scale' },
  wemwbs: {
    defaultMessage: 'WEMWBS (Mental wellbeing)',
    description: 'Name of the Warwick-Edinburgh Mental Wellbeing Scale',
  },
  ftnd: { defaultMessage: 'FTND', description: 'Name of the FTND assessment' },
  electronicFtnd: { defaultMessage: 'E-FTND', description: 'Name of the electronic FTND assessment' },
  best: { defaultMessage: 'BEST', description: 'Name of the Borderline Evaluation of Severity Over Time assessment' },
  ders: { defaultMessage: 'DERS', description: 'Name of the Difficulties in Emotion Regulation Scale' },
  leaps: { defaultMessage: 'LEAPS', description: 'Name of the LEAPS work impairment assessment' },
  bam: { defaultMessage: 'Brief Addiction Monitor (BAM)', description: 'Name of the Brief Addiction Monitor' },
  treatmentUtilization: {
    defaultMessage: 'Treatment Utilization',
    description: 'Name of the treatment utilization measure',
  },
  allianceQuestions: {
    defaultMessage: 'Alliance Questions',
    description: 'Name of the grouped therapeutic alliance responses',
  },
  psc17: { defaultMessage: 'PSC-17 (Pediatric Symptom Checklist)', description: 'Name of the PSC-17 assessment' },
  cssrs: {
    defaultMessage: 'C-SSRS (Columbia-Suicide Severity Rating Scale)',
    description: 'Name of the C-SSRS assessment',
  },
  csi16: { defaultMessage: 'CSI-16 (Couples Satisfaction Index)', description: 'Name of the CSI-16 assessment' },
  spin: { defaultMessage: 'SPIN (Social Phobia Inventory)', description: 'Name of the SPIN assessment' },

  // PHQ supplemental
  lifeImpairmentQuestion: {
    defaultMessage: 'Life Impairment Question',
    description: 'Label for the PHQ-9 life impairment supplemental score',
  },
  thoughtsOfSelfHarm: {
    defaultMessage: 'Thoughts of self harm and/or suicide',
    description: 'Label for a positive PHQ-9 self-harm supplemental score',
  },

  // LEAPS supplemental
  percentNonWorkImpairment: {
    defaultMessage: 'Percent Non-work Impairment',
    description: 'Label for the LEAPS non-work impairment supplemental score',
  },
  productivitySubscale: {
    defaultMessage: 'Productivity Subscale',
    description: 'Label for the LEAPS productivity supplemental score',
  },

  // DERS supplemental labels match the chart legend.
  dersAwareness: { defaultMessage: 'Awareness', description: 'Label for the DERS awareness subscale' },
  dersClarity: { defaultMessage: 'Clarity', description: 'Label for the DERS clarity subscale' },
  dersGoals: { defaultMessage: 'Goals', description: 'Label for the DERS goals subscale' },
  dersImpulse: { defaultMessage: 'Impulse', description: 'Label for the DERS impulse subscale' },
  dersNonacceptance: { defaultMessage: 'Nonacceptance', description: 'Label for the DERS nonacceptance subscale' },
  dersStrategies: { defaultMessage: 'Strategies', description: 'Label for the DERS strategies subscale' },

  // BEST supplemental labels match the chart legend.
  bestThoughtsAndFeelings: {
    defaultMessage: 'Thoughts and Feelings',
    description: 'Label for the BEST thoughts and feelings subscale',
  },
  bestPositiveBehaviors: {
    defaultMessage: 'Effective Behaviors',
    description: 'Label for the BEST effective behaviors subscale',
  },
  bestNegativeBehaviors: {
    defaultMessage: 'Behavioral Symptoms',
    description: 'Label for the BEST behavioral symptoms subscale',
  },

  // BAM supplemental
  bamRisk: { defaultMessage: 'BAM Risk factors', description: 'Label for the BAM risk factors score' },
  bamProtective: {
    defaultMessage: 'BAM Protective factors',
    description: 'Label for the BAM protective factors score',
  },

  // Treatment utilization supplemental
  roiErVisits: { defaultMessage: 'ER Visits', description: 'Label for the treatment utilization ER visits score' },
  roiHlocWeeks: {
    defaultMessage: 'HLOC Weeks',
    description: 'Label for the treatment utilization higher level of care weeks score',
  },
  roiOutpatientVisits: {
    defaultMessage: 'Outpatient Visits',
    description: 'Label for the treatment utilization outpatient visits score',
  },
  roiPartialWeeks: {
    defaultMessage: 'Partial/IOP/Residential Weeks',
    description: 'Label for the treatment utilization partial, IOP or residential weeks score',
  },

  // Alliance supplemental
  approachSatisfaction: {
    defaultMessage: '{program} Approach Satisfaction',
    description: 'Label for an alliance response about the provider approach',
  },
  relationshipSatisfaction: {
    defaultMessage: 'Relationship Satisfaction',
    description: 'Label for an alliance response about the provider relationship',
  },
  contentSatisfaction: {
    defaultMessage: '{program} Content Satisfaction',
    description: 'Label for an alliance response about therapy or coaching content',
  },
})

/** Score key whose band table describes all three alliance responses. */
const SATISFACTION_DESCRIPTION_SCORE_ID = 'SATISFACTION'

export const SELF_REFLECTION_MEASURE_ID = 'SELF-REFLECTION'

const notPerformanceCoaching = ({ programTaxonomy }: MeasureContext) =>
  !programTaxonomy || !isCTPOForProgram(programTaxonomy, ProgramNames.PerformanceCoaching)

/** Alliance scores are only visible to the owning provider or a supervisor. */
const ownClientOrSupervisor = ({ providerId, isSupervisor }: MeasureContext, outcome?: Outcome) =>
  Boolean(isSupervisor) ||
  (providerId !== undefined && outcome?.provider_lyra_id !== undefined && providerId === outcome.provider_lyra_id)

/**
 * Display order. Mirrors the order the legacy ClientOutcomes table used, with the grouped
 * measures (BEST/DERS/LEAPS/BAM/Treatment Utilization) and Alliance at the end.
 */
export const MEASURES: readonly MeasureDefinition[] = [
  { id: 'AUDIT-C', name: MESSAGES.auditC, scoreId: 'AUDIT-C', maxScore: 12, chartKey: 'AUDIT-C' },
  { id: 'CGSQ', name: MESSAGES.cgsq, scoreId: 'CGSQ', chartKey: 'CGSQ' },
  {
    id: 'PARENTAL-STRESS',
    name: MESSAGES.parentalStress,
    scoreId: 'PARENTAL-STRESS',
    maxScore: 90,
    chartKey: 'PARENTAL-STRESS',
  },
  { id: 'PARENTING-TASK', name: MESSAGES.parentingTask, scoreId: 'PARENTING-TASK', chartKey: 'PARENTING-TASK' },
  { id: 'RCADS-TOTAL', name: MESSAGES.rcadsTotal, scoreId: 'RCADS-TOTAL', maxScore: 75, metadataTokens: ['RCADS25'] },
  {
    id: 'RCADS-ANXIETY',
    name: MESSAGES.rcadsAnxiety,
    scoreId: 'RCADS-ANXIETY',
    maxScore: 45,
    chartKey: 'RCADS-ANXIETY',
    metadataTokens: ['RCADS25'],
  },
  {
    id: 'RCADS-DEPRESSION',
    name: MESSAGES.rcadsDepression,
    scoreId: 'RCADS-DEPRESSION',
    maxScore: 30,
    chartKey: 'RCADS-DEPRESSION',
    metadataTokens: ['RCADS25'],
  },
  {
    id: 'RCADS-P-TOTAL',
    name: MESSAGES.rcadsParentTotal,
    scoreId: 'RCADS-P-TOTAL',
    maxScore: 75,
    metadataTokens: ['RCADSP25'],
  },
  {
    id: 'RCADS-P-ANXIETY',
    name: MESSAGES.rcadsParentAnxiety,
    scoreId: 'RCADS-P-ANXIETY',
    maxScore: 45,
    chartKey: 'RCADS-P-ANXIETY',
    metadataTokens: ['RCADSP25'],
  },
  {
    id: 'RCADS-P-DEPRESSION',
    name: MESSAGES.rcadsParentDepression,
    scoreId: 'RCADS-P-DEPRESSION',
    maxScore: 30,
    chartKey: 'RCADS-P-DEPRESSION',
    metadataTokens: ['RCADSP25'],
  },
  { id: 'TFM', name: MESSAGES.tfm, scoreId: 'TFM', chartKey: 'TFM' },
  { id: 'TLFB-ALCOHOL', name: MESSAGES.tlfbAlcohol, scoreId: 'TLFB-ALCOHOL', chartKey: 'TLFB-ALCOHOL' },
  {
    id: 'TLFB-OTHER-SUBSTANCES',
    name: MESSAGES.tlfbSubstances,
    scoreId: 'TLFB-OTHER-SUBSTANCES',
    maxScore: 7,
    chartKey: 'TLFB-OTHER-SUBSTANCES',
  },
  { id: 'BDD', name: MESSAGES.bdd, scoreId: 'BDD', maxScore: 30, chartKey: 'BDD' },
  { id: 'PACS', name: MESSAGES.pacs, scoreId: 'PACS', maxScore: 30, chartKey: 'PACS' },
  { id: 'ADCS', name: MESSAGES.adcs, scoreId: 'ADCS' },
  { id: 'WSAS', name: MESSAGES.wsas, scoreId: 'WSAS', maxScore: 40, chartKey: 'WSAS' },
  { id: 'PCL5', name: MESSAGES.pcl5, scoreId: 'PCL5', maxScore: 80, chartKey: 'PCL5' },
  {
    id: 'CHRT',
    name: MESSAGES.chrt,
    scoreId: 'CHRT',
    maxScore: 48,
    chartKey: 'CHRT',
    hasCriticalResponses: true,
  },
  {
    id: 'GAD',
    name: MESSAGES.gad7,
    scoreId: 'GAD',
    maxScore: 21,
    chartKey: 'GAD',
  },
  {
    id: 'PHQ',
    name: MESSAGES.phq9,
    scoreId: 'PHQ',
    maxScore: 27,
    chartKey: 'PHQ',
    supplemental: [
      { id: 'PHQ-LIFE', label: MESSAGES.lifeImpairmentQuestion, format: 'description' },
      { id: 'PHQ-SELF-HARM', label: MESSAGES.thoughtsOfSelfHarm, format: 'description', omitWhenZero: true },
    ],
  },
  {
    id: 'PSS',
    name: MESSAGES.pss,
    scoreId: 'PSS',
    maxScore: 40,
    chartKey: 'PSS',
  },
  {
    id: 'WELL-BEING',
    name: MESSAGES.wemwbs,
    scoreId: 'WELL-BEING',
    maxScore: 70,
    chartKey: 'WELL-BEING',
  },
  { id: 'FTND', name: MESSAGES.ftnd, scoreId: 'FTND' },
  { id: 'E-FTND', name: MESSAGES.electronicFtnd, scoreId: 'E-FTND' },
  {
    id: 'ALLIANCE',
    name: MESSAGES.allianceQuestions,
    responseDetailsKey: 'ALLIANCE',
    responseDetailsKeys: ['SATISFACTION-GOALS', 'SATISFACTION-RELATIONSHIP', 'SATISFACTION-THERAPY'],
    hideFinalScore: true,
    supplemental: [
      {
        id: 'SATISFACTION-GOALS',
        label: MESSAGES.approachSatisfaction,
        format: 'description',
        descriptionScoreId: SATISFACTION_DESCRIPTION_SCORE_ID,
      },
      {
        id: 'SATISFACTION-RELATIONSHIP',
        label: MESSAGES.relationshipSatisfaction,
        format: 'description',
        descriptionScoreId: SATISFACTION_DESCRIPTION_SCORE_ID,
      },
      {
        id: 'SATISFACTION-THERAPY',
        label: MESSAGES.contentSatisfaction,
        format: 'description',
        descriptionScoreId: SATISFACTION_DESCRIPTION_SCORE_ID,
      },
    ],
    visibleWhen: ownClientOrSupervisor,
  },
  {
    id: 'BEST-TOTAL',
    name: MESSAGES.best,
    scoreId: 'BEST-TOTAL',
    maxScore: 72,
    chartKey: 'BEST-TOTAL',
    metadataTokens: ['BEST'],
    supplemental: [
      { id: 'BEST-THOUGHTS-FEELINGS', label: MESSAGES.bestThoughtsAndFeelings, maxScore: 40 },
      { id: 'BEST-POSITIVE-BEHAVIORS', label: MESSAGES.bestPositiveBehaviors, maxScore: 15 },
      { id: 'BEST-NEGATIVE-BEHAVIORS', label: MESSAGES.bestNegativeBehaviors, maxScore: 20 },
    ],
  },
  {
    id: 'DERS-TOTAL',
    name: MESSAGES.ders,
    scoreId: 'DERS-TOTAL',
    maxScore: 90,
    chartKey: 'DERS-TOTAL',
    metadataTokens: ['DERS'],
    supplemental: [
      { id: 'DERS-AWARENESS', label: MESSAGES.dersAwareness, maxScore: 15 },
      { id: 'DERS-CLARITY', label: MESSAGES.dersClarity, maxScore: 15 },
      { id: 'DERS-GOALS', label: MESSAGES.dersGoals, maxScore: 15 },
      { id: 'DERS-IMPULSE', label: MESSAGES.dersImpulse, maxScore: 15 },
      { id: 'DERS-NONACCEPTANCE', label: MESSAGES.dersNonacceptance, maxScore: 15 },
      { id: 'DERS-STRATEGIES', label: MESSAGES.dersStrategies, maxScore: 15 },
    ],
  },
  {
    id: 'LEAPS',
    name: MESSAGES.leaps,
    scoreId: 'LEAPS',
    maxScore: 28,
    chartKey: 'LEAPS',
    supplemental: [
      { id: 'LEAPS-NW-IMP', label: MESSAGES.percentNonWorkImpairment, format: 'percent' },
      { id: 'LEAPS-PRD-SS', label: MESSAGES.productivitySubscale },
    ],
    visibleWhen: notPerformanceCoaching,
  },
  {
    id: 'BAM',
    name: MESSAGES.bam,
    scoreId: 'BAM-USE',
    maxScore: 12,
    chartKey: 'BAM-USE',
    responseDetailsKey: 'BAM',
    supplemental: [
      { id: 'BAM-RISK', label: MESSAGES.bamRisk, maxScore: 24 },
      { id: 'BAM-PROTECTIVE', label: MESSAGES.bamProtective, maxScore: 24 },
    ],
  },
  {
    id: 'ROI-TOTAL',
    name: MESSAGES.treatmentUtilization,
    responseDetailsKey: 'ROI-TOTAL',
    metadataTokens: ['ROI'],
    supplemental: [
      { id: 'ROI-ER-VISITS', label: MESSAGES.roiErVisits },
      { id: 'ROI-HLOC-WEEKS', label: MESSAGES.roiHlocWeeks },
      { id: 'ROI-OUTPATIENT-VISITS', label: MESSAGES.roiOutpatientVisits },
      { id: 'ROI-PARTIAL-IOP-RESIDENTIAL-WEEKS', label: MESSAGES.roiPartialWeeks },
    ],
  },
  { id: 'PSC-17', name: MESSAGES.psc17, scoreId: 'PSC-17', maxScore: 34 },
  // Intake and ideation-severity C-SSRS forms use different scales, so keep the raw score.
  { id: 'C-SSRS', name: MESSAGES.cssrs, scoreId: 'C-SSRS' },
  { id: 'CSI-16', name: MESSAGES.csi16, scoreId: 'CSI-16', maxScore: 81 },
  { id: 'SPIN', name: MESSAGES.spin, scoreId: 'SPIN', maxScore: 68 },
]

const normalize = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, '')

const CLINICAL_MEASURE_TOKENS = new Set<string>(Object.values(CLINICAL_MEASURES))

/** Exact row ids resolve automatically; only differing assignment tokens need aliases. */
const getMetadataTokens = (measure: MeasureDefinition): readonly string[] =>
  measure.metadataTokens ?? (CLINICAL_MEASURE_TOKENS.has(measure.id) ? [measure.id] : [])

/** Arrays allow one assignment token to resolve to multiple rows when a measure needs it. */
const MEASURES_BY_TOKEN: ReadonlyMap<string, readonly MeasureDefinition[]> = MEASURES.reduce((byToken, measure) => {
  for (const token of getMetadataTokens(measure)) {
    const key = normalize(token)
    byToken.set(key, [...(byToken.get(key) ?? []), measure])
  }
  return byToken
}, new Map<string, MeasureDefinition[]>())

/**
 * Resolve a `clinicalMeasuresIncluded` token to the measures it declares.
 *
 * Returns an empty list for tokens we do not model. Callers must not fall back to displaying the
 * raw token — it is an internal identifier, not a clinical name.
 */
export const resolveMetadataToken = (token: string): readonly MeasureDefinition[] =>
  MEASURES_BY_TOKEN.get(normalize(token)) ?? []

/** Every score key a measure reads, primary and supplemental. Used to pick its latest outcome. */
export const getMeasureScoreIds = (measure: MeasureDefinition): readonly ScoreId[] => [
  ...(measure.scoreId ? [measure.scoreId] : []),
  ...(measure.supplemental ?? []).map(({ id }) => id),
]

export const isMeasureVisible = (measure: MeasureDefinition, context: MeasureContext, outcome?: Outcome) =>
  measure.visibleWhen?.(context, outcome) ?? true
